<template>
  <header class="site-nav" :class="{ scrolled: isScrolled, open: menuOpen }">
    <div class="container nav-inner">
      <router-link to="/" class="brand" @click="menuOpen = false">
        <span class="brand-mark">
          <svg viewBox="0 0 28 28" fill="none" aria-hidden="true">
            <path
              d="M14 2l10 5.5v11L14 24l-10-5.5v-11L14 2z"
              stroke="currentColor"
              stroke-width="2"
            />
            <circle cx="14" cy="14" r="3.4" fill="currentColor" />
          </svg>
        </span>
        <span class="brand-text">
          <b>浩连科技</b>
          <i>HOLINK TECH</i>
        </span>
      </router-link>

      <nav class="menu" aria-label="主导航">
        <router-link
          v-for="item in nav"
          :key="item.path"
          :to="item.path"
          class="menu-item"
        >
          {{ item.name }}
        </router-link>
      </nav>

      <div class="nav-actions">
        <router-link to="/contact" class="btn btn-primary nav-cta">免费咨询</router-link>
        <button
          class="menu-toggle"
          :aria-expanded="menuOpen"
          aria-label="打开菜单"
          @click="menuOpen = !menuOpen"
        >
          <span></span><span></span><span></span>
        </button>
      </div>
    </div>

    <transition name="drawer">
      <div v-if="menuOpen" class="drawer">
        <div class="drawer-inner">
          <router-link
            v-for="item in nav"
            :key="item.path"
            :to="item.path"
            class="drawer-item"
            @click="menuOpen = false"
          >
            {{ item.name }}
          </router-link>
          <router-link to="/contact" class="btn btn-primary drawer-cta" @click="menuOpen = false">
            免费咨询
          </router-link>
        </div>
      </div>
    </transition>
  </header>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import site from '@/data/site'

const nav = site.nav
const isScrolled = ref(false)
const menuOpen = ref(false)

const onScroll = () => {
  isScrolled.value = window.scrollY > 20
}

onMounted(() => {
  window.addEventListener('scroll', onScroll, { passive: true })
  onScroll()
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScroll)
})
</script>

<style scoped>
.site-nav {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  height: var(--nav-h);
  transition: all 0.35s ease;
  background: rgba(10, 30, 60, 0.35);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

.site-nav.scrolled {
  background: rgba(255, 255, 255, 0.94);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  box-shadow: 0 6px 24px rgba(10, 30, 60, 0.08);
}

.nav-inner {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}

/* 品牌 */
.brand {
  display: flex;
  align-items: center;
  gap: 10px;
}

.brand-mark {
  width: 42px;
  height: 42px;
  border-radius: 11px;
  display: grid;
  place-items: center;
  color: #fff;
  background: linear-gradient(135deg, var(--hl-blue), var(--hl-cyan));
  box-shadow: 0 6px 18px rgba(11, 92, 255, 0.4);
  flex-shrink: 0;
}

.brand-mark svg {
  width: 24px;
  height: 24px;
}

.brand-text {
  display: flex;
  flex-direction: column;
  line-height: 1.15;
  color: #fff;
  transition: color 0.3s;
}

.brand-text b {
  font-size: 19px;
  font-weight: 800;
  letter-spacing: 1px;
}

.brand-text i {
  font-style: normal;
  font-family: var(--font-display);
  font-size: 10px;
  letter-spacing: 3px;
  color: var(--hl-cyan);
}

.scrolled .brand-text {
  color: var(--hl-text);
}

/* 菜单 */
.menu {
  display: flex;
  align-items: center;
  gap: 6px;
}

.menu-item {
  position: relative;
  padding: 8px 16px;
  font-size: 15px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.92);
  transition: color 0.3s;
}

.menu-item::after {
  content: '';
  position: absolute;
  left: 16px;
  right: 16px;
  bottom: 2px;
  height: 2px;
  border-radius: 2px;
  background: var(--hl-cyan);
  transform: scaleX(0);
  transform-origin: center;
  transition: transform 0.3s ease;
}

.menu-item:hover,
.menu-item.router-link-exact-active {
  color: #fff;
}

.menu-item.router-link-exact-active::after,
.menu-item:hover::after {
  transform: scaleX(1);
}

.scrolled .menu-item {
  color: var(--hl-text);
}

.scrolled .menu-item:hover,
.scrolled .menu-item.router-link-exact-active {
  color: var(--hl-blue);
}

/* 右侧按钮 */
.nav-actions {
  display: flex;
  align-items: center;
  gap: 14px;
}

.nav-cta {
  padding: 10px 24px;
  font-size: 14px;
}

.menu-toggle {
  display: none;
  flex-direction: column;
  justify-content: center;
  gap: 5px;
  width: 42px;
  height: 42px;
  padding: 8px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.1);
}

.menu-toggle span {
  height: 2px;
  width: 100%;
  background: #fff;
  border-radius: 2px;
  transition: all 0.3s;
}

.scrolled .menu-toggle {
  background: var(--hl-bg-soft);
}

.scrolled .menu-toggle span {
  background: var(--hl-text);
}

.site-nav.open .menu-toggle span:nth-child(1) {
  transform: translateY(7px) rotate(45deg);
}

.site-nav.open .menu-toggle span:nth-child(2) {
  opacity: 0;
}

.site-nav.open .menu-toggle span:nth-child(3) {
  transform: translateY(-7px) rotate(-45deg);
}

/* 抽屉 */
.drawer {
  position: fixed;
  top: var(--nav-h);
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--hl-dark);
  z-index: 99;
  overflow-y: auto;
}

.drawer-inner {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.drawer-item {
  padding: 16px 18px;
  font-size: 18px;
  font-weight: 600;
  color: #fff;
  border-radius: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
}

.drawer-item.router-link-exact-active {
  color: var(--hl-cyan);
}

.drawer-cta {
  margin-top: 18px;
  justify-content: center;
}

.drawer-enter-active,
.drawer-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.drawer-enter-from,
.drawer-leave-to {
  opacity: 0;
  transform: translateY(-12px);
}

/* 响应式 */
@media (max-width: 992px) {
  .menu {
    display: none;
  }
  .menu-toggle {
    display: flex;
  }
}

@media (max-width: 768px) {
  .nav-cta {
    display: none;
  }
  .brand-text b {
    font-size: 17px;
  }
}
</style>
